import { useState } from 'react';
import { useAuth } from '../App';
import posthog from 'posthog-js';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Crown, Check, Lock, Zap, FileText, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Paywall({ children }) {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // If user is Pro, show the app
  if (user?.subscription_tier === 'pro') {
    return children;
  }

  // Track paywall view (once per mount)
  try { posthog.capture('paywall_viewed', { plan_id: 'pro_monthly' }); } catch (e) {}

  const handleSubscribe = async () => {
    setLoading(true);
    try { posthog.capture('checkout_started', { plan_id: 'pro_monthly' }); } catch (e) {}
    try {
      const token = localStorage.getItem('auth_token');
      // Use /pay/now endpoint - fresh path
      const response = await axios.post(`${API}/pay/now`, {
        plan_id: 'pro_monthly',
        origin_url: window.location.origin
      }, { 
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="paywall">
      <div className="max-w-lg w-full">
        {/* Lock Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 border-2 border-primary flex items-center justify-center">
            <Lock className="w-10 h-10 text-primary" />
          </div>
        </div>

        {/* Heading */}
        <h1 className="font-heading text-3xl md:text-4xl text-center mb-4">
          UNLOCK LIFE ADMIN
        </h1>
        <p className="text-center text-muted-foreground mb-8">
          Subscribe to access unlimited autofills and save hours on every application.
        </p>

        {/* Pricing Card */}
        <div className="border-2 border-primary p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-yellow-500" />
              <span className="font-heading text-xl">PRO ACCESS</span>
            </div>
            <div className="text-right">
              <span className="font-heading text-3xl">$9.99</span>
              <span className="text-muted-foreground">/mo</span>
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            <li className="flex items-center gap-3">
              <Check className="w-5 h-5 text-accent" />
              <span>Unlimited autofills</span>
            </li>
            <li className="flex items-center gap-3">
              <Check className="w-5 h-5 text-accent" />
              <span>AI document parsing</span>
            </li>
            <li className="flex items-center gap-3">
              <Check className="w-5 h-5 text-accent" />
              <span>All application templates</span>
            </li>
            <li className="flex items-center gap-3">
              <Check className="w-5 h-5 text-accent" />
              <span>Priority processing</span>
            </li>
            <li className="flex items-center gap-3">
              <Check className="w-5 h-5 text-accent" />
              <span>Cancel anytime</span>
            </li>
          </ul>

          <Button 
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full brutalist-shadow uppercase tracking-wider text-sm py-6"
            data-testid="subscribe-btn"
          >
            {loading ? 'LOADING...' : 'SUBSCRIBE NOW'}
          </Button>
        </div>

        {/* Trust Badges */}
        <div className="flex justify-center gap-8 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>SECURE PAYMENT</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <span>INSTANT ACCESS</span>
          </div>
        </div>
      </div>
    </div>
  );
}
